import axios from "axios";

// Send credentials (HttpOnly cookies) on EVERY axios request. The Bearer
// token in localStorage is a fallback for environments where third-party
// cookies are blocked (GHL iframe). Setting this default makes the auth
// resilient: if the Bearer token gets cleared mid-session by a transient
// 401, the cookie still keeps the user logged in.
axios.defaults.withCredentials = true;

// Key used to persist the session token in localStorage.
// This is the Bearer-token fallback for environments where third-party
// cookies are blocked (e.g. the app embedded in a cross-origin iframe
// inside GoHighLevel — Safari/Chrome ITP blocks HttpOnly cookies there).
export const AUTH_TOKEN_KEY = "bdd_session_token";

export function getStoredToken() {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function setStoredToken(token) {
  try {
    if (token) localStorage.setItem(AUTH_TOKEN_KEY, token);
    else localStorage.removeItem(AUTH_TOKEN_KEY);
  } catch (e) {
    // quota/privacy-mode errors are recoverable; the user simply won't have a persisted session
    console.debug("setStoredToken:", e?.message || e);
  }
}

export function clearStoredToken() {
  setStoredToken(null);
}

// Global axios request interceptor: attach Bearer token from localStorage
// on every request. Cookies are still sent via withCredentials for the
// non-iframe case, so this is purely additive.
axios.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// On 401, drop the stale Bearer token ONLY for the auth check endpoint.
// Data endpoints can return 401 for transient reasons (e.g. backend
// reload mid-request); clearing the token then cascades into a logout
// loop. The /api/auth/me check is the canonical "is this session
// valid?" probe, so that's the safe place to clear.
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    if (error?.response?.status === 401) {
      const url = error?.config?.url || "";
      if (url.includes("/api/auth/me")) {
        clearStoredToken();
      }
    }
    return Promise.reject(error);
  }
);
